import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { DatePicker, DatesRangeValue, DateValue } from "@mantine/dates";
import type { IFormikProps, IProps } from "./date-picker-input.types";
import { Input } from "../input";
import { Popover } from "../popover";
import { faCalendar, faChevronLeft, faChevronRight, faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import { Icon } from "../icon";
import { DropdownIcon } from "../dropdown-icon";
import { format } from "date-fns";
import { ActionIcon } from "../action-icon";
import { Group } from "../group";
import { Menu } from "../menu";
import { addCustomDayProps, formatDate, getMaxDate, getMinDate, toLocalDate } from "./date-picker-input.utilities";
import { Text } from "../text";
import { TimePicker } from "../time-picker";
import styles from "./date-picker-input.module.scss";
import {
    MAX_HOURS,
    MAX_MILLISECONDS,
    MAX_MINUTES_OR_SECONDS,
    RelativeDates,
    relativeDatesInfo,
    TIME_RANGE_TOOLTIP,
} from "./date-picker-input.constants";
import { useField, useFormikContext } from "formik";
import { kebabCase } from "lodash";
import { Stack } from "../stack";
import { Button } from "../button";
import { Tooltip } from "../tooltip";
import dayjs from "dayjs";

/**
 * DatePickerInput component allows users to select a date range with optional time selection.
 * It supports relative date presets and custom date ranges.
 *
 * @param {IProps} props - The properties for the DatePickerInput component.
 * @param {Date} props.startDate - The initial start date of the range.
 * @param {Date} props.endDate - The initial end date of the range.
 * @param {number} [props.limitSelectionMonths] - The maximum number of months allowed for selection.
 * @param {number} [props.limitPastDays] - The number of past days to limit the selection.
 * @param {RelativeDates} [props.relativeDateTime] - The relative date preset to use.
 * @param {boolean} [props.showRelativeDateValues] - Whether to show relative date presets.
 * @param {boolean} [props.allowTimeSelect]
 * @param {Date} [props.maxDate] - The maximum date allowed for selection.
 * @param {RelativeDates[]} [props.omittedRelativeDates] - The relative dates to omit from the dropdown.
 * @returns {JSX.Element} The rendered DatePickerInput component.
 */
const DatePickerInput = ({
    startDate,
    endDate,
    limitSelectionMonths,
    limitPastDays,
    relativeDateTime,
    showRelativeDateValues,
    allowTimeSelect,
    startDateName = "startDateTime",
    endDateName = "endDateTime",
    relativeDateName = "relativeDateTime",
    variant,
    disabled,
    activable,
    onChange,
    displayIcon = true,
    maxDate = new Date(),
    infoLabel,
    transformRelativeDateTime = false,
    omittedRelativeDates,
}: IProps) => {
    const [opened, setOpened] = useState(false);
    const { setFieldValue } = useFormikContext();

    const [value, setValue] = useState<DatesRangeValue<DateValue>>([new Date(), null]);

    const [selectedRelativeDateTime, setSelectedRelativeDateTime] = useState<RelativeDates | null>(null);

    const [startTime, setStartTime] = useState<string | undefined>(undefined);
    const [endTime, setEndTime] = useState<string | undefined>(undefined);

    const onPresetSelect = useCallback((label: RelativeDates) => {
        const presetRange = relativeDatesInfo[label];
        setValue([presetRange.startDate, presetRange.endDate]);
        setSelectedRelativeDateTime(label);
        setStartTime(format(presetRange.startDate, "HH:mm"));
        setEndTime(format(presetRange.endDate, "HH:mm"));
    }, []);

    const setInitialDate = useCallback(() => {
        // Set to the original startDate, endDate & times or to default values
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            setValue([start, end]);

            setStartTime(format(start, "HH:mm"));
            setEndTime(format(end, "HH:mm"));
        } else {
            const { startDate, endDate } = relativeDateTime
                ? relativeDatesInfo[relativeDateTime]
                : relativeDatesInfo.Last14Days;
            setValue([startDate, endDate]);
            setStartTime(format(startDate, "HH:mm"));
            setEndTime(format(endDate, "HH:mm"));
        }
    }, [endDate, relativeDateTime, startDate]);

    useEffect(() => {
        if (relativeDateTime) setSelectedRelativeDateTime(relativeDateTime);

        setInitialDate();
    }, [relativeDateTime, setInitialDate]);

    const handleDateChange = useCallback((newValue: DatesRangeValue<DateValue>) => {
        setSelectedRelativeDateTime(null);
        if (newValue[0] === null && newValue[1] === null) {
            setValue([new Date(), null]);
        } else {
            setValue(newValue);
        }
    }, []);

    const onCancel = useCallback(() => {
        setInitialDate();
        setOpened(false);
        setSelectedRelativeDateTime(null);
    }, [setInitialDate]);

    const displayText = useMemo(() => {
        if (relativeDateTime) {
            return relativeDatesInfo[relativeDateTime].formatedName;
        }

        const formattedStartDate = startDate ? format(new Date(startDate), "dd/MM/yy") : "";
        const formattedEndDate = endDate ? format(new Date(endDate), "dd/MM/yy") : "";
        return `${formattedStartDate} - ${formattedEndDate}`;
    }, [startDate, endDate, relativeDateTime]);

    const relativeDatesToShow = useMemo(() => {
        return (Object.keys(relativeDatesInfo) as RelativeDates[]).filter(
            (label) => !omittedRelativeDates?.includes(label)
        );
    }, [omittedRelativeDates]);

    const handleStartTimeChange = useCallback((value: string) => {
        setSelectedRelativeDateTime(null);
        setStartTime(value);
    }, []);

    const handleEndTimeChange = useCallback((value: string) => {
        setSelectedRelativeDateTime(null);
        setEndTime(value);
    }, []);

    /** Time range is not valid when:
     * - The start time is after the end time.
     * - The start time is the same as the end time and the start date is the same as the end date.
     */
    const isTimeRangeValid = useMemo(() => {
        if (!allowTimeSelect || !value[0] || !value[1]) return true;
        const start = dayjs(value[0]);
        const end = dayjs(value[1]);
        if (start.isAfter(end, "day")) return false;
        if (start.isSame(end, "day") && startTime === endTime) return false;
        return true;
    }, [allowTimeSelect, value, startTime, endTime]);

    return (
        <Popover
            position="bottom-start"
            shadow="md"
            disabled={disabled}
            opened={opened}
            onExitTransitionEnd={setInitialDate}
            onDismiss={() => setOpened(false)}
        >
            <Popover.Target>
                <Input
                    active={activable && (typeof startTime !== "undefined" || typeof endTime !== "undefined")}
                    leftSection={displayIcon ? <Icon icon={faCalendar} size="sm" /> : undefined}
                    rightSection={<DropdownIcon isOpen={opened} />}
                    component="button"
                    pointer
                    type="button"
                    onClick={() => setOpened(!opened)}
                    data-testid="date-range-filter"
                    variant={variant}
                    disabled={disabled}
                >
                    {displayText}
                </Input>
            </Popover.Target>
            <Popover.Dropdown bdrs="lg" p="0" data-testid="date-picker-container">
                <Group className={styles["relative-dates-container"]} gap="md" bdrs="lg">
                    {showRelativeDateValues && (
                        <Stack p="sm" gap="0" miw={100}>
                            <Menu>
                                {relativeDatesToShow.map((label) => (
                                    <Menu.Item
                                        key={label}
                                        onClick={() => onPresetSelect(label)}
                                        active={selectedRelativeDateTime === label}
                                        data-testid={`date-options_${kebabCase(label)}`}
                                    >
                                        {relativeDatesInfo[label].formatedName}
                                    </Menu.Item>
                                ))}
                            </Menu>
                        </Stack>
                    )}

                    <Stack className={styles["date-picker-container"]} p="lg" bdrs="lg">
                        {typeof limitSelectionMonths !== "undefined" && limitSelectionMonths > 0 && (
                            <Group gap="sm">
                                <Icon className={styles["info-icon"]} icon={faCircleInfo} size="xxs" />
                                <Text className={styles["info-text"]} size="xs">
                                    {infoLabel ??
                                        `Maximum range is ${limitSelectionMonths} month${limitSelectionMonths === 1 ? "" : "s"}.`}
                                </Text>
                            </Group>
                        )}
                        <DatePicker
                            onChange={handleDateChange}
                            value={value}
                            type="range"
                            minDate={getMinDate({
                                limitPastDays,
                                limitSelectionMonths,
                                rangeStart: value[0] !== null && value[1] === null ? dayjs(value[0]) : null,
                            })}
                            maxDate={getMaxDate({
                                maxDate: dayjs(maxDate),
                                limitSelectionMonths,
                                rangeStart: value[1] === null ? (value[0] !== null ? dayjs(value[0]) : null) : null,
                            })}
                            defaultDate={new Date(new Date().setMonth(new Date().getMonth() - 1))}
                            previousIcon={
                                <ActionIcon variant="default" data-testid="previous-month-button">
                                    <Icon icon={faChevronLeft} size="xxs" />
                                </ActionIcon>
                            }
                            previousLabel="Previous Month"
                            nextIcon={
                                <ActionIcon variant="default" data-testid="next-month-button">
                                    <Icon icon={faChevronRight} size="xxs" />
                                </ActionIcon>
                            }
                            nextLabel="Next Month"
                            numberOfColumns={2}
                            getDayAriaLabel={(date) => `date-picker_${kebabCase(formatDate(date))}`}
                            getDayProps={(date) => addCustomDayProps(date)}
                            allowSingleDateInRange
                            classNames={{
                                day: styles["day"],
                                monthRow: styles["monthRow"],
                                monthCell: styles["monthCell"],
                            }}
                        />
                        <Group gap="sm" mt="auto" align="flex-end" justify="space-between">
                            {allowTimeSelect && (
                                <Group gap="sm" w={225}>
                                    <Input.Wrapper variant="filled" flex={1} label={formatDate(value[0])}>
                                        <TimePicker
                                            value={startTime}
                                            onChange={handleStartTimeChange}
                                            variant="filled"
                                            data-testid="date-picker-input-start-time"
                                            error={!isTimeRangeValid ? true : undefined}
                                        />
                                    </Input.Wrapper>

                                    <Input.Wrapper
                                        variant="filled"
                                        flex={1}
                                        label={value[1] ? formatDate(value[1]) : "No date selected"}
                                    >
                                        <TimePicker
                                            value={endTime}
                                            onChange={handleEndTimeChange}
                                            variant="filled"
                                            data-testid="date-picker-input-end-time"
                                            error={!isTimeRangeValid ? true : undefined}
                                        />
                                    </Input.Wrapper>
                                </Group>
                            )}

                            <Group gap="sm" ml="auto">
                                <Button onClick={onCancel} variant="default" data-testid="cancel-button">
                                    Cancel
                                </Button>
                                <Tooltip label={TIME_RANGE_TOOLTIP} disabled={isTimeRangeValid}>
                                    <Button
                                        onClick={() => {
                                            const startDateTime = toLocalDate(value[0]);
                                            const endDateTime = toLocalDate(value[1]);

                                            if (startDateTime && startTime) {
                                                const [hours, minutes] = startTime.split(":").map(Number);
                                                startDateTime.setHours(hours, minutes, 0, 0);
                                            }

                                            if (endDateTime && endTime) {
                                                const [hours, minutes] = endTime.split(":").map(Number);
                                                /** If the end time is 23:59, set the seconds and milliseconds to the max values e.g. 23:59:59.999 */
                                                const shouldSetSecondsAndMilliseconds =
                                                    hours === MAX_HOURS && minutes === MAX_MINUTES_OR_SECONDS;
                                                endDateTime.setHours(
                                                    hours,
                                                    minutes,
                                                    shouldSetSecondsAndMilliseconds ? MAX_MINUTES_OR_SECONDS : 0,
                                                    shouldSetSecondsAndMilliseconds ? MAX_MILLISECONDS : 0
                                                );
                                            }

                                            setOpened(false);
                                            if (selectedRelativeDateTime) {
                                                setFieldValue(relativeDateName, selectedRelativeDateTime);
                                                setFieldValue(
                                                    startDateName,
                                                    transformRelativeDateTime
                                                        ? relativeDatesInfo[selectedRelativeDateTime].startDate
                                                        : undefined
                                                );
                                                setFieldValue(
                                                    endDateName,
                                                    transformRelativeDateTime
                                                        ? relativeDatesInfo[selectedRelativeDateTime].endDate
                                                        : undefined
                                                );
                                                onChange?.(null, null, selectedRelativeDateTime);
                                            } else if (startDateTime && endDateTime) {
                                                setFieldValue(startDateName, startDateTime);
                                                setFieldValue(endDateName, endDateTime);
                                                setFieldValue(relativeDateName, undefined);
                                                onChange?.(startDateTime, endDateTime, null);
                                            }
                                        }}
                                        disabled={!isTimeRangeValid}
                                        data-testid="done-button"
                                    >
                                        Done
                                    </Button>
                                </Tooltip>
                            </Group>
                        </Group>
                    </Stack>
                </Group>
            </Popover.Dropdown>
        </Popover>
    );
};

const DatePickerInputFormik: FunctionComponent<IFormikProps> = ({ validate, ...otherProps }) => {
    const [startDateField] = useField(otherProps.startDateName);
    const [endDateField] = useField(otherProps.endDateName);
    const [relativeDateField] = useField(otherProps.relativeDateName ?? "relativeDateTime");
    return (
        <Input.Formik.Wrapper name={otherProps.startDateName} validate={validate}>
            <DatePickerInput
                startDate={startDateField.value}
                endDate={endDateField.value}
                relativeDateTime={relativeDateField.value}
                {...otherProps}
            />
        </Input.Formik.Wrapper>
    );
};

DatePickerInput.Formik = DatePickerInputFormik;

export { DatePickerInput };

export default DatePickerInput;
